import { useMemo } from "react";
import { DataTable, type Column, type SortDirection } from "@/components/ui/data-table";
import { keyEventsInfo, sessionsInfo } from "@/components/report/metric-info";
import type { ReportCountryRow } from "@/lib/api";

interface CountryTableProps {
  rows: ReportCountryRow[];
  /** Threaded to the Conversions column tooltip for the GA4 deep-link. */
  propertyId?: string;
}

export function CountryTable({ rows, propertyId }: CountryTableProps) {
  const totalSessions = useMemo(() => rows.reduce((acc, r) => acc + r.sessions, 0), [rows]);

  const columns = useMemo<Column<ReportCountryRow>[]>(
    () => [
      {
        id: "country",
        header: "Country",
        sortable: true,
        accessorKey: "country",
        cell: (row) => (
          <span className="font-semibold text-neutral-900">{row.country || "(unknown)"}</span>
        ),
      },
      {
        id: "sessions",
        header: "Sessions",
        sortable: true,
        accessorKey: "sessions",
        info: sessionsInfo,
        cell: (row) => (
          <CountryShareCell
            sessions={row.sessions}
            share={totalSessions > 0 ? row.sessions / totalSessions : 0}
          />
        ),
      },
      {
        id: "keyEvents",
        header: "Conversions",
        sortable: true,
        accessorKey: "keyEvents",
        info: keyEventsInfo(propertyId),
        cell: (row) => <span className="tabular-nums">{row.keyEvents.toLocaleString()}</span>,
      },
    ],
    [totalSessions, propertyId],
  );

  if (rows.length === 0) {
    return (
      <div className="rounded-xl bg-white p-8 text-center ring-1 ring-neutral-950/5">
        <p className="font-display text-base font-semibold text-neutral-950">
          No geography data this period
        </p>
        <p className="mt-2 text-sm text-neutral-600 text-pretty">
          Once visitors arrive, the countries driving the most social sessions will line up here.
        </p>
      </div>
    );
  }

  return (
    <DataTable<ReportCountryRow>
      columns={columns}
      data={rows.slice(0, 25)}
      getRowId={(r) => r.country}
      defaultSortColumn="sessions"
      defaultSortDirection="desc"
      sortComparator={comparator}
    />
  );
}

interface CountryShareCellProps {
  sessions: number;
  share: number;
}

function CountryShareCell({ sessions, share }: CountryShareCellProps) {
  return (
    <span className="inline-flex flex-col items-start gap-1 tabular-nums">
      <span>{sessions.toLocaleString()}</span>
      <span
        aria-hidden="true"
        className="block h-1 w-32 overflow-hidden rounded-full bg-neutral-100"
      >
        <span
          className="block h-full rounded-full bg-purple-500"
          style={{ width: `${Math.max(2, Math.round(share * 100))}%` }}
        />
      </span>
    </span>
  );
}

function comparator(
  a: ReportCountryRow,
  b: ReportCountryRow,
  column: string,
  direction: SortDirection,
): number {
  if (!direction) return 0;
  const dir = direction === "asc" ? 1 : -1;
  switch (column) {
    case "country":
      return a.country.localeCompare(b.country) * dir;
    case "sessions":
      return (a.sessions - b.sessions) * dir;
    case "keyEvents":
      return (a.keyEvents - b.keyEvents) * dir;
    default:
      return 0;
  }
}
